import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { EnterSubmitDirective } from '../../core/directives/enter-submit.directive';

export interface AssignOption {
  id: string | number;
  label: string;
}

export interface AssignRelationDialogData {
  title: string;
  label: string;
  options: AssignOption[];
  emptyMessage: string;
  actionLabel: string;
}

@Component({
  selector: 'app-assign-relation-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatAutocompleteModule, MatFormFieldModule, MatInputModule, TranslatePipe, EnterSubmitDirective],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <form appEnterSubmit [formGroup]="form" (ngSubmit)="submit()">
      <mat-dialog-content>
        @if (data.options.length > 0) {
          <mat-form-field class="full-width">
            <mat-label>{{ data.label }}</mat-label>
            <input
              matInput
              type="text"
              formControlName="selectedOption"
              [matAutocomplete]="optionsAutocomplete"
              [placeholder]="'common.search' | t"
            />
            <mat-autocomplete #optionsAutocomplete="matAutocomplete" [displayWith]="displayOption">
              @for (option of filteredOptions; track option.id) {
                <mat-option [value]="option">{{ option.label }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        } @else {
          <p>{{ data.emptyMessage }}</p>
        }
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="dialogRef.close(null)">{{ 'common.cancel' | t }}</button>
        <button mat-flat-button type="submit" color="primary" [disabled]="selectedOptionId === null">
          {{ data.actionLabel }}
        </button>
      </mat-dialog-actions>
    </form>
  `,
  styles: `
    .full-width {
      width: 100%;
      min-width: 280px;
    }
  `
})
export class AssignRelationDialogComponent {
  readonly selectedOption = new FormControl<AssignOption | string | null>(null, Validators.required);
  readonly form = new FormGroup({ selectedOption: this.selectedOption });

  constructor(
    readonly dialogRef: MatDialogRef<AssignRelationDialogComponent, string | number | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: AssignRelationDialogData
  ) {}

  readonly displayOption = (option: AssignOption | string | null): string =>
    typeof option === 'string' ? option : option?.label ?? '';

  get filteredOptions(): AssignOption[] {
    const query = this.displayOption(this.selectedOption.value).trim().toLocaleLowerCase();
    if (!query || typeof this.selectedOption.value !== 'string') {
      return this.data.options;
    }
    return this.data.options.filter((option) => option.label.toLocaleLowerCase().includes(query));
  }

  get selectedOptionId(): string | number | null {
    const selected = this.selectedOption.value;
    return selected && typeof selected !== 'string' ? selected.id : null;
  }

  submit(): void {
    const id = this.selectedOptionId;
    if (id !== null) {
      this.dialogRef.close(id);
    }
  }
}
